import React, { useState, useEffect } from "react";
import api from "../../../chat/api";
import { isPlanLimitError } from "../../../../events/planLimitEvents";
import toast from "react-hot-toast";
import Cookies from "js-cookie";

const AddSubsectionModal = ({
  isOpen,
  onClose,
  onSubmit,
  sectionTitle, 
  sectionId,
  folderId,
  previousIndex,
  token,
  refreshAnalysisData,
}) => {
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTitle("");
      setPrompt("");
      setIsLoading(false);
      setIsSuccess(false);
    }
  }, [isOpen]);

  // Reset success state after 3 seconds and close modal
  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        setIsSuccess(false);
        onClose();
        if (refreshAnalysisData) {
          refreshAnalysisData();
        }
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isSuccess, onClose, refreshAnalysisData]);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    if (!sectionId || !folderId) return;

    setIsLoading(true);

    try {
      // Prepare request body according to API spec
      const requestBody = {
        previous_index: previousIndex,
        subsection_titles: [title.trim()],
        subsection_queries: [prompt.trim() || ""]
      };

      const organizationId = Cookies.get("selected_org_id");
      const url = organizationId
        ? `api/v1/rfp/folders/${folderId}/analysis/add-section/?organization_id=${encodeURIComponent(organizationId)}`
        : `api/v1/rfp/folders/${folderId}/analysis/add-section/`;
      const response = await api.post(
        url,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        // Call the original onSubmit if provided
        if (onSubmit) {
          onSubmit({
            title: title.trim(),
            prompt: prompt.trim(),
            sectionId,
            apiResponse: response.data
          });
        }
        
        // Show success state
        setIsSuccess(true);
        setIsLoading(false);
      } else {
        throw new Error(`Server returned status ${response.status}`);
      }
    } catch (error) {
      console.error('Error adding subsection:', error);
      
      let errorMessage = "Failed to add subsection";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      if (!isPlanLimitError(error)) toast.error(errorMessage);
      setIsLoading(false);
      setIsSuccess(false);
    }
  };

  const handleCancel = () => {
    if (!isLoading && !isSuccess) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop - slightly lighter blur/overlay per screenshot */}
      <div
        className="absolute inset-0 bg-black/20 backdrop-sm"
        onClick={handleCancel}
      />

      {/* Modal Container */}
      <div className="relative z-10 w-[530px] bg-white rounded-[12px] shadow-2xl overflow-hidden py-6 px-6 font-outfit">
        {/* Header */}
        <div className="flex justify-between items-start mb-5">
          <div className="space-y-1">
            <h2 className="text-[18px] font-semibold text-[#171717]">
              Add Subsection
            </h2>
            <p className="text-[#52525B] text-[15px]">
              Adding to: <span className="font-medium text-[#444444]">{sectionTitle}</span>
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="text-[#171717] hover:opacity-60 transition-opacity disabled:opacity-50"
            disabled={isLoading || isSuccess}
          >
            {/* Thinner X icon to match screenshot */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Form Fields */}
        <div className="space-y-6">
          <div>
            <label className="block text-[#52525B] font-outfit text-[15px] font-medium leading-none mb-2">
              Subsection Title
            </label>
            <input
              type="text"
              placeholder="Enter subsection title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isLoading || isSuccess}
              className="
                w-full
                h-11
                box-border
                px-4
                rounded-[10px]
                border border-[#E5E7EB]
                text-[15px]
                leading-[1.2]
                font-normal
                placeholder:text-[#0000004C]
                focus:outline-none
                focus:ring-1 focus:ring-[#D8A7E0]
                focus:border-[#D8A7E0]
                hover:text-[15px]
                focus:text-[15px]
                autofill:text-[15px]
                appearance-none
                disabled:bg-gray-100 disabled:cursor-not-allowed
              "
            />
          </div>

          <div>
            <label className="block text-[#52525B] font-outfit text-[15px] font-medium leading-none mb-2">
              AI Prompt Used <span className="text-[#0000004C] font-normal">(Optional)</span>
            </label>
            <textarea
              rows={5}
              placeholder="Add custom instructions for AI to generate subsection..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={isLoading || isSuccess}
              className="w-full px-4 py-3 rounded-[10px] border border-[#E5E7EB] text-[15px] focus:outline-none focus:ring-1 focus:ring-[#D8A7E0] placeholder:text-[#0000004C] resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex gap-4 justify-center">
          <button
            onClick={handleCancel}
            disabled={isLoading || isSuccess}
            className=" w-[20%] py-2  rounded-[8px] border border-[#C95EBE] text-[#C95EBE] font-medium text-[15px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSuccess ? "Close" : "Cancel"}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || isLoading || isSuccess}
            className={`w-[35%] py-2 rounded-[8px] font-medium text-[15px] ${
              isSuccess 
                ? 'bg-[#C95EBE] !text-white' 
                : 'bg-[#C95EBE] !text-white'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <span>Adding...</span>
              </div>
            ) : isSuccess ? (
              "Added"
            ) : (
              "Add Subsection"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSubsectionModal;
